// Type definitions
interface StorageInterface {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface StorageFactory {
  get<T>(key: string, fallback: T): T;
  set(key: string, value: unknown): void;
  remove(key: string): void;
}

/**
 * Safely execute a callback function without throwing errors
 * @param callback - Function to execute safely
 * @returns The result of the callback or null if an error occurs
 */
const safe = <T>(callback: () => T): T | null => {
  try {
    return callback();
  } catch {
    return null;
  }
};

/**
 * Create a storage interface with safe JSON parsing and stringifying
 * @param storageType - Storage implementation (localStorage, sessionStorage, etc.)
 * @returns Object with get, set, and remove methods
 */
export const handleStorageFactory = (
  storageType: StorageInterface
): StorageFactory => {
  /**
   * Safely get and parse value from storage or return fallback
   * @param key - Storage key
   * @param fallback - Fallback value to return if key doesn't exist or parsing fails
   * @returns Parsed value from storage or the fallback value
   */
  const get = <T>(key: string, fallback: T): T => {
    const item = safe(() => storageType.getItem(key));
    if (item === null) {
      return fallback;
    }

    const data = safe(() => JSON.parse(item));
    return data !== null ? (data as T) : fallback;
  };

  /**
   * Safely set and stringify value to storage
   * @param key - Storage key
   * @param value - Value to store (will be JSON.stringified)
   */
  const set = (key: string, value: unknown): void => {
    safe(() => storageType.setItem(key, JSON.stringify(value)));
  };

  /**
   * Safely remove value from storage
   * @param key - Storage key to remove
   */
  const remove = (key: string): void => {
    safe(() => storageType.removeItem(key));
  };

  return {
    get,
    set,
    remove,
  };
};
